import { db } from '@/services/firebase'
import { collection, getDocs, doc, getDoc, addDoc, updateDoc, deleteDoc, writeBatch, increment, serverTimestamp, setDoc } from 'firebase/firestore'
import { getFunctions, httpsCallable } from 'firebase/functions'

export default {
  namespaced: true,
  
  state: {
    events: [],
    currentEvent: null,
    loading: false,
    error: null
  },

  mutations: {
    SET_EVENTS(state, events) {
      state.events = events
    },
    SET_CURRENT_EVENT(state, event) {
      state.currentEvent = event
    },
    CLEAR_CURRENT_EVENT(state) {
      state.currentEvent = null
    },
    SET_LOADING(state, status) {
      state.loading = status
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    UPDATE_BOOKED_COUNT(state, { eventId, booked }) {
      if (state.currentEvent && state.currentEvent.id === eventId) {
        state.currentEvent.booked = booked
      }
      
      // Also update in the events array if needed
      const eventIndex = state.events.findIndex(e => e.id === eventId)
      if (eventIndex !== -1) {
        state.events[eventIndex].booked = booked
      }
    }
  },

  actions: {
    async fetchEvents({ commit }) {
      commit('SET_LOADING', true)
      try {
        // Clear existing events before fetching new ones
        commit('SET_EVENTS', [])
        const querySnapshot = await getDocs(collection(db, 'events'))
        const events = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        commit('SET_EVENTS', events)
      } catch (error) {
        commit('SET_ERROR', error.message)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async fetchEventById({ commit }, eventId) {
      commit('SET_LOADING', true)
      try {
        const docRef = doc(db, 'events', eventId)
        const docSnap = await getDoc(docRef)
        if (docSnap.exists()) {
          commit('SET_CURRENT_EVENT', {
            id: docSnap.id,
            ...docSnap.data()
          })
        }
      } catch (error) {
        commit('SET_ERROR', error.message)
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async createEvent({ commit }, eventData) {
      commit('SET_LOADING', true)
      try {
        if (!eventData.title || !eventData.description || !eventData.date) {
          throw new Error('Missing required fields')
        }
        
        // Set default image if none provided
        if (!eventData.imageUrl) {
          eventData.imageUrl = 'https://picsum.photos/800/400'
        }
        
        const docRef = await addDoc(collection(db, 'events'), eventData)
        return docRef.id
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async updateEvent({ commit }, eventData) {
      commit('SET_LOADING', true)
      try {
        const { id, ...data } = eventData
        await updateDoc(doc(db, 'events', id), data)
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async deleteEventById({ commit }, eventId) {
      commit('SET_LOADING', true)
      try {
        await deleteDoc(doc(db, 'events', eventId))
      } catch (error) {
        commit('SET_ERROR', error.message)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async bookTickets({ commit }, { eventId, quantity, orderId, paypalCaptureId, email, userId }) {
      try {
        // Get event details for price information
        const eventRef = doc(db, 'events', eventId);
        const eventDoc = await getDoc(eventRef);
        
        if (!eventDoc.exists()) {
          throw new Error('Event not found');
        }
        
        const eventData = eventDoc.data();
        const eventPrice = parseFloat(eventData.price) || 0;
        const totalAmount = eventPrice * quantity;
        const isFreeEvent = eventPrice === 0;
        
        // Begin batch operation for atomic updates
        const batch = writeBatch(db);
        
        // 1. Update event's booked count
        batch.update(eventRef, {
          booked: increment(quantity)
        });

        // 2. Create a booking record
        const bookingData = {
          eventId,
          quantity,
          orderId,
          email,
          userId: userId || null,
          timestamp: serverTimestamp(),
          eventTitle: eventData.title,
          eventDate: eventData.date
        };

        // Add the booking document
        const bookingsRef = collection(db, 'bookings');
        const bookingDocRef = doc(bookingsRef);
        batch.set(bookingDocRef, bookingData);
        
        // 3. Create transaction record with payment details
        const transactionData = {
          bookingId: bookingDocRef.id,
          eventId,
          userId: userId || null,
          email,
          amount: totalAmount,
          currency: 'GBP', // Assuming GBP based on the UI showing £
          quantity,
          paymentMethod: isFreeEvent ? 'free' : 'paypal',
          paymentStatus: 'completed',
          orderId: orderId || 'free-event',
          timestamp: serverTimestamp(),
          lastUpdated: serverTimestamp(),
          eventTitle: eventData.title,
          eventDate: eventData.date
        };
        
        // Add additional PayPal specific data if this was a paid transaction
        if (!isFreeEvent && orderId) {
          transactionData.paymentDetails = {
            provider: 'paypal',
            providerTransactionId: orderId,
            // Store capture ID if available
            providerCaptureId: paypalCaptureId || null
          };
          
          // Also store capture ID at the top level for easier access
          if (paypalCaptureId) {
            transactionData.paypalCaptureId = paypalCaptureId;
          }
        }
        
        // Add the transaction document
        const transactionsRef = collection(db, 'transactions');
        const transactionDocRef = doc(transactionsRef);
        batch.set(transactionDocRef, transactionData);
        
        // 4. If user is logged in, create user-specific records
        if (userId) {
          // Ensure user document exists
          const userDocRef = doc(db, 'users', userId);
          batch.set(userDocRef, { 
            lastActivity: serverTimestamp(),
            hasBookings: true,
            email: email // Store email to make it easier to find user's transactions
          }, { merge: true });
          
          // Add booking to user's bookings subcollection
          const userBookingDocRef = doc(collection(db, `users/${userId}/bookings`), bookingDocRef.id);
          batch.set(userBookingDocRef, {
            ...bookingData,
            bookingId: bookingDocRef.id,
            createdAt: serverTimestamp()
          });
          
          // Add transaction to user's transactions subcollection
          const userTransactionDocRef = doc(collection(db, `users/${userId}/transactions`), transactionDocRef.id);
          batch.set(userTransactionDocRef, {
            ...transactionData,
            transactionId: transactionDocRef.id,
            createdAt: serverTimestamp()
          });
        }
        
        // Commit all operations together
        await batch.commit();
        
        console.log(`Booking created with ID: ${bookingDocRef.id}`);
        console.log(`Transaction created with ID: ${transactionDocRef.id}`);
        
        return {
          bookingId: bookingDocRef.id,
          transactionId: transactionDocRef.id
        };
      } catch (error) {
        console.error('Error booking tickets:', error);
        throw error;
      }
    },
    
    async processRefund({ commit }, { transactionId, refundAmount, reason }) {
      try {
        // Get the transaction record
        const transactionRef = doc(db, 'transactions', transactionId);
        const transactionDoc = await getDoc(transactionRef);
        
        if (!transactionDoc.exists()) {
          throw new Error('Transaction not found');
        }
        
        const transactionData = transactionDoc.data();
        
        // Validate the refund amount
        if (refundAmount > transactionData.amount) {
          throw new Error('Refund amount cannot exceed the original payment amount');
        }
        
        // Free events cannot be refunded
        if (transactionData.paymentMethod === 'free') {
          throw new Error('Cannot refund free events');
        }
        
        // Create a refund record in the refunds collection FIRST, with 'pending' status
        const refundData = {
          transactionId,
          bookingId: transactionData.bookingId,
          eventId: transactionData.eventId,
          userId: transactionData.userId,
          email: transactionData.email,
          amount: refundAmount,
          currency: transactionData.currency || 'gbp',
          reason: reason,
          timestamp: serverTimestamp(),
          originalPaymentMethod: transactionData.paymentMethod,
          originalOrderId: transactionData.orderId,
          status: 'pending' // Start with pending status
        };
        
        const refundsRef = collection(db, 'refunds');
        const refundRef = await addDoc(refundsRef, refundData);
        const refundId = refundRef.id;
        
        console.log(`Created pending refund record with ID: ${refundId}`);
        
        // If this is a PayPal transaction, process the refund through PayPal API
        if (transactionData.paymentMethod === 'paypal' && transactionData.orderId) {
          try {
            // Get Firebase Functions instance
            const functions = getFunctions();
            
            // Detect if we're in a local development environment
            const isLocalDev = window.location.hostname === 'localhost' || 
                              window.location.hostname === '127.0.0.1';
            
            // Call the Cloud Function to process the PayPal refund
            const processPayPalRefund = httpsCallable(functions, 'processPayPalRefund');
            console.log(`Calling processPayPalRefund function with refundId: ${refundId}, using ${isLocalDev ? 'TEST mode' : 'PRODUCTION mode'}`);
            
            // First try getting debug information to diagnose the issue
            if (isLocalDev) {
              console.log('Running in debug mode to diagnose auth issues...');
              try {
                const debugResult = await processPayPalRefund({ 
                  testMode: 'debug'
                });
                console.log('Debug information returned:', debugResult.data);
              } catch (debugError) {
                console.error('Debug mode failed:', debugError);
              }
            }
            
            // If in local development environment, use test mode to bypass actual PayPal API calls
            const result = await processPayPalRefund({ 
              refundId: refundId,
              testMode: isLocalDev // Use test mode in local development
            });
            
            console.log('PayPal refund processed:', result.data);
            
            // Check if refund was successful
            if (result.data && result.data.success) {
              // NOW update the transaction record AFTER successful PayPal refund
              await updateDoc(transactionRef, {
                paymentStatus: refundAmount === transactionData.amount ? 'refunded' : 'partially_refunded',
                refundAmount: refundAmount,
                refundReason: reason,
                refundDate: serverTimestamp(),
                lastUpdated: serverTimestamp(),
                refundId: refundId // Link to the refund record
              });
              
              // If the user has a record of this transaction, update that too
              if (transactionData.userId) {
                const userTransactionRef = doc(db, `users/${transactionData.userId}/transactions`, transactionId);
                await updateDoc(userTransactionRef, {
                  paymentStatus: refundAmount === transactionData.amount ? 'refunded' : 'partially_refunded',
                  refundAmount: refundAmount,
                  refundReason: reason,
                  refundDate: serverTimestamp(),
                  lastUpdated: serverTimestamp(),
                  refundId: refundId // Link to the refund record
                });
              }
            } else {
              // Refund was not successful - don't update transaction status
              console.error('PayPal refund was not successful:', result.data);
              throw new Error(result.data?.message || 'PayPal refund failed, but no error message was provided.');
            }
            
          } catch (paypalError) {
            console.error('Error processing PayPal refund:', paypalError);
            
            // Mark the refund record with the error
            await updateDoc(doc(db, 'refunds', refundId), {
              paypalError: paypalError.message || 'Unknown PayPal error',
              status: 'error'
            });
            
            // Don't update the transaction status, just throw the error
            throw new Error(`PayPal refund failed: ${paypalError.message || 'See Firebase console logs for details'}. The transaction has NOT been refunded. Please try again or contact support.`);
          }
        } else {
          // For non-PayPal transactions, update the status immediately
          await updateDoc(transactionRef, {
            paymentStatus: refundAmount === transactionData.amount ? 'refunded' : 'partially_refunded',
            refundAmount: refundAmount,
            refundReason: reason,
            refundDate: serverTimestamp(),
            lastUpdated: serverTimestamp(),
            refundId: refundId // Link to the refund record
          });
          
          // If the user has a record of this transaction, update that too
          if (transactionData.userId) {
            const userTransactionRef = doc(db, `users/${transactionData.userId}/transactions`, transactionId);
            await updateDoc(userTransactionRef, {
              paymentStatus: refundAmount === transactionData.amount ? 'refunded' : 'partially_refunded',
              refundAmount: refundAmount,
              refundReason: reason,
              refundDate: serverTimestamp(),
              lastUpdated: serverTimestamp(),
              refundId: refundId // Link to the refund record
            });
          }
          
          // Update the refund status to completed
          await updateDoc(doc(db, 'refunds', refundId), {
            status: 'completed'
          });
        }
        
        return refundId;
      } catch (error) {
        console.error('Error processing refund:', error);
        throw error;
      }
    },
    
    async getUserTransactions({ commit }, userId) {
      try {
        if (!userId) {
          throw new Error('User ID is required');
        }
        
        const userTransactionsRef = collection(db, `users/${userId}/transactions`);
        const querySnapshot = await getDocs(userTransactionsRef);
        
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('Error fetching user transactions:', error);
        throw error;
      }
    }
  },

  getters: {
    upcomingEvents: (state) => {
      const now = new Date();
      
      // Helper function to get a valid date from an event
      const getEventDate = (event) => {
        const date = new Date(event.date || event.startDate || event.startTime);
        return !isNaN(date.getTime()) ? date : now;
      };
      
      // Helper function to get end date or calculate it from start date
      const getEventEndDate = (event) => {
        // Try to use explicit end date if available
        const endDate = new Date(event.endDate || event.end || event.endTime);
        
        if (!isNaN(endDate.getTime())) {
          return endDate;
        }
        
        // If no end date, assume event lasts 2 hours from start
        const startDate = getEventDate(event);
        const calculatedEndDate = new Date(startDate);
        calculatedEndDate.setHours(calculatedEndDate.getHours() + 2);
        return calculatedEndDate;
      };
      
      return state.events
        .filter(event => {
          // Show events that haven't ended yet
          return getEventEndDate(event) > now;
        })
        .sort((a, b) => {
          // Sort by start date in ascending order
          return getEventDate(a) - getEventDate(b);
        });
    },
    getEventById: (state) => (id) => {
      return state.events.find(event => event.id === id);
    }
  }
} 